import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  ConvenienceDispatchFixedRouteDetailEntity,
  ConvenienceDispatchFixedRouteStopEntity,
  ConvenienceDispatchFixedRouteVehicleEntity,
} from 'src/entities/ConvenienceDispatchFixedRoute.entity';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { PlanningsDeliveryWithLegRunEntity, PlanningsLegRunOperationEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { compareTrucksByDisplayOrder } from 'src/utils/truckDisplayOrder.util';

import { buildCanonicalCoordKeys } from './canonicalCoordKey';
import { fixedRoutePlaceholderDeliveryId } from './placeholderDeliveryIds';
import { PlaceSide, coordKeyOf, placeKeyFromCanonical, placeKeyOf, placeNameOf } from './placeKeys';

/**
 * 中央ボードの 1 カード単位。地点ごとにまとめた表示用 VM。
 * V3Plans の Place grouping と同じ考え方:
 *   連続した同地点・同 action の operations を 1 card にまとめる。
 */
export type VehicleRowCardVM = {
  /**
   * - loading / unloading: 実 operation に基づくカード
   * - no_delivery: 固定ルートに存在するが legRun が今日存在しない（本日なし）
   * - template: 固定ルートに存在しており legRun は未割当（仮カード、薄く表示）
   */
  kind: 'loading' | 'unloading' | 'no_delivery' | 'template';
  /**
   * 固定ルート由来の template / no_delivery カードについて、元の stop が積地か降地かを保持する。
   * (kind=loading/unloading は実 operation 由来なので未設定)。
   * placeLegRunInRow で「ドロップ時に土台の積地カードを除去する」判定に使う。
   */
  stopType?: 'loading' | 'unloading';
  /**
   * grouping key: 既定は lat-lng ベース（位置情報未取得時はフォールバック文字列）。
   * 「配送リストの地点表示 = 名称でまとめる」設定では末尾に地点名が付き、同一座標でも
   * 名称が違えば別カードになる。共同納品先・まとめ選択の突合もこのキーで行う。
   */
  placeKey: string;
  /**
   * このカードにマージされた operation の「元の地点キー」。`placeKey` が代表座標キーに
   * 寄せられて（log_web_issues#322）吸収された側のキーが消えるのを補う。
   *
   * Why 必要か: 同一地点タグ（#318 の `buildSameLocationAssignments`）と拠点推定
   * （`estimateHubPlaceKeys`）は未割当案件の生の地点キーとカードのキーを突き合わせる。
   * カードは 1 枚 = `placeKey` 1 本なので、統合すると「吸収された側の座標と一致する
   * 未割当案件」を検出できなくなる。索引側で別名も引けるようにする。
   *
   * 実 operation 由来のカードだけが持つ（固定ルートの仮カードは判定対象外）。
   */
  placeKeyAliases?: string[];
  placeName: string;
  /** 代表 arrivalAt の HH:mm 文字列。no_delivery は null */
  timeLabel: string | null;
  /** このカードに含まれる legRun の item_count 合計 */
  itemCount: number;
  operations: PlanningsLegRunOperationEntity[];
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  /** 固定ルート外の operation が 1 件でも含まれる (Board 側で fixedRouteSelected と AND して使う) */
  isRouteDeviation: boolean;
  isAutoAssigned: boolean;
};

export type VehicleRowVM = {
  deliveryId: number;
  truckId: number;
  /** この行 (= その勤務) に乗るドライバー。1 台に複数ドライバーが乗る勤務があるため行が持つ */
  driverId?: number | null;
  rotationNumber: number;
  totalRotations: number;
  driverColor: string;
  truckColor: string | null;
  totalItemCount: number;
  /** 積地・降地・本日なし をまとめた表示用カードリスト。Board はこれを描画する */
  cards: VehicleRowCardVM[];
  /**
   * 勤務開始の完全な ISO 日時（delivery.startAt）。展開行の計画開始に使う。
   * 既存の勤務時刻の経路（buildVehicleExtraMap / buildShiftTimeByTruckId）は
   * すべて HH:mm に落としており、「勤務開始 23:00 と翌 01:00 のどちらが先か」を
   * 判別できないため ISO のまま持つ 1 本を足している。
   * delivery を持たない placeholder 行では undefined。
   */
  planStartAt?: string;
  /** fixed route 専用の合成行。実在する delivery ではないため選択・集計対象から除外する */
  isPlaceholderRow: boolean;
  /** ローカルで「回転追加」ボタン経由で挿入された仮想 row。
   *  isPlaceholderRow=true と併用するが、同一 truck の代表 delivery にぶら下がるので
   *  「全選択」など UI 操作の対象には含める (isPlaceholderRow=true && isExtraRotation=false の
   *   FR 合成行とは扱いを分ける)。 */
  isExtraRotation?: boolean;
  /**
   * 「回転を追加」で足した行が、どの勤務 (delivery) に属するか。
   * deliveryId は負のダミーなので、追加先はこちらで引く。
   */
  extraOfDeliveryId?: number;
};

/** operation から見た地点の側。キーの導出は placeKeys.ts に集約している */
const sideOf = (op: PlanningsLegRunOperationEntity): PlaceSide => (op.action === '積' ? 'loading' : 'unloading');

/** legRun が引けない operation は自身の id で 1 枚のカードになる（他と混ざらせない） */
const opFallback = (op: PlanningsLegRunOperationEntity): string => `op-${op.id}`;

const buildPlaceName = (op: PlanningsLegRunOperationEntity, legRun: ShipmentOrderLegRunForPlanEntity | undefined) =>
  placeNameOf(sideOf(op), legRun);

/**
 * その row の operation 列から「operationId → 代表の座標キー」を作る。
 * 精度違いで割れた同一地点を、配車アルゴリズムの移動時間0 でまとめ直す
 * （logposeTech/log_web_issues#322）。判定規則は canonicalCoordKey.ts を参照。
 */
const buildCanonicalCoordKeyMap = (
  sortedOps: readonly PlanningsLegRunOperationEntity[],
  legRunMap: Map<number, ShipmentOrderLegRunForPlanEntity>,
) => buildCanonicalCoordKeys(sortedOps.map((op) => ({
  id: op.id,
  action: op.action,
  departureAt: op.departureAt,
  drivingDurationSeconds: op.drivingDurationSeconds,
  coordKey: coordKeyOf(sideOf(op), legRunMap.get(op.shipmentOrderLegRunId), opFallback(op)),
})));

/**
 * 固定ルートの stop 用の place key を作る。
 * 同じ「論理的な地点」(同一 name + 同一 lat/lng) は同じ placeKey を返し、
 * mergeOrPushExtra で隣接カードがマージできるようにする。
 * 名前が違う場合は座標が同じでも別 placeKey にする（store_name 単位で識別）。
 *
 * 同 rotation 内に同名 stop が非連続で複数現れたケース (例: A → B → A の往復) は
 * 同じ placeKey で 2 カード生成されるので、React key 側で cardIndex を加えて
 * 一意化する（buildDeliveryCard / noDeliveryCardId 参照）。
 */
function buildFrPlaceKey(
  stopType: 'loading' | 'unloading',
  name: string,
  matchingLegRuns: ShipmentOrderLegRunForPlanEntity[],
): string {
  for (const lr of matchingLegRuns) {
    if (stopType === 'loading' && lr.loading_lat != null && lr.loading_lng != null) {
      return `${stopType}-${name}-${lr.loading_lat}-${lr.loading_lng}`;
    }
    if (stopType === 'unloading' && lr.unloading_lat != null && lr.unloading_lng != null) {
      return `${stopType}-${name}-${lr.unloading_lat}-${lr.unloading_lng}`;
    }
  }
  return `${stopType}-${name}`;
}

/**
 * row 全体で (kind, placeKey) が一致するカードがあれば集約する (連続要件なし)。
 * 同一拠点 (lat/lng 同一) は表記ゆれの name 違いを跨いでも 1 カードに揃える。
 * V3Plans (地図画面) のクラスタリングと同じ粒度に合わせる狙い。
 * 仮カードは legRun を重複排除しつつ追加集約、itemCount を再計算する。
 */
function mergeOrPushExtra(cards: VehicleRowCardVM[], next: VehicleRowCardVM): void {
  const existingIdx = cards.findIndex((c) => c.kind === next.kind && c.placeKey === next.placeKey);
  if (existingIdx >= 0) {
    const existing = cards[existingIdx];
    const seenIds = new Set<number | undefined>(existing.legRuns.map((lr) => lr.id));
    for (const lr of next.legRuns) {
      if (!seenIds.has(lr.id)) {
        existing.legRuns.push(lr);
        seenIds.add(lr.id);
      }
    }
    existing.itemCount = existing.legRuns.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0);
    return;
  }
  cards.push(next);
}

/** 別名キーを重複なく足す。未設定のカード（固定ルートの仮カード）にも足せる形にする */
function pushPlaceKeyAlias(card: VehicleRowCardVM, key: string): void {
  const aliases = card.placeKeyAliases;
  if (aliases == null) {
    card.placeKeyAliases = [key];
    return;
  }
  if (!aliases.includes(key)) aliases.push(key);
}

/**
 * arrivalAt → departureAt → id 順にソート済みの operations を地点単位の
 * VehicleRowCardVM[] にまとめる。
 * row 全体で同地点 (kind + placeKey が一致) の operations は 1 card にマージされる。
 * V3Plans (地図画面) と同じく lat/lng 同一を 1 拠点とみなし、表記ゆれの name 違いも統合する。
 *
 * 座標は精度違いで割れることがあるので、キーには生の座標ではなく代表座標キーを使う
 * (buildCanonicalCoordKeyMap)。代表は列の連続性から決まるため、渡す列は
 * ソート済みであること（入力順に落ちると代表が揺れる）。
 */
function groupOpsToCards(
  sortedOps: PlanningsLegRunOperationEntity[],
  legRunMap: Map<number, ShipmentOrderLegRunForPlanEntity>,
  grouping: DeliveryListGroupingValues,
): VehicleRowCardVM[] {
  const cards: VehicleRowCardVM[] = [];
  const indexByKey = new Map<string, number>();
  const canonicalCoordKeys = buildCanonicalCoordKeyMap(sortedOps, legRunMap);

  for (const op of sortedOps) {
    const legRun = legRunMap.get(op.shipmentOrderLegRunId);
    const coordKey = canonicalCoordKeys.get(op.id) ?? coordKeyOf(sideOf(op), legRun, opFallback(op));
    const key = placeKeyFromCanonical(sideOf(op), legRun, coordKey, grouping);
    // 代表へ寄せる前のキー。索引側で別名として引けるようにカードへ残す
    const rawKey = placeKeyOf(sideOf(op), legRun, opFallback(op), grouping);
    const kind: 'loading' | 'unloading' = op.action === '積' ? 'loading' : 'unloading';
    const cacheKey = `${kind}-${key}`;
    const existingIdx = indexByKey.get(cacheKey);

    if (existingIdx != null) {
      // 同 row 内・同地点・同 action は (連続でなくても) 既存カードにマージする
      const existing = cards[existingIdx];
      existing.operations.push(op);
      if (legRun) existing.legRuns.push(legRun);
      existing.itemCount += legRun?.item_count ?? 0;
      pushPlaceKeyAlias(existing, rawKey);
      if (!op.fixed) existing.isRouteDeviation = true;
      if (!op.manualPlanning && op.algorithmRequestId > 0) existing.isAutoAssigned = true;
    } else {
      indexByKey.set(cacheKey, cards.length);
      cards.push({
        kind,
        placeKey: key,
        placeKeyAliases: [rawKey],
        placeName: buildPlaceName(op, legRun),
        timeLabel: datetimeDecorator.toHourMinutes(new Date(op.arrivalAt)) ?? null,
        itemCount: legRun?.item_count ?? 0,
        operations: [op],
        legRuns: legRun ? [legRun] : [],
        isRouteDeviation: !op.fixed,
        isAutoAssigned: !op.manualPlanning && op.algorithmRequestId > 0,
      });
    }
  }

  return cards;
}

/**
 * deliveries と fixedRouteDetail を合成して VehicleRowVM[] を構築する。
 *
 * ## row の単位
 * `delivery` ではなく `truckId + cycle` が 1 row。
 * 1 delivery の中に cycle=1 と cycle=2 が混在する場合は 2 row を生成する。
 *
 * ## 生成フロー
 * 1. 各 delivery の operations を cycle ごとに分解し、row 候補を作る
 * 2. fixed route detail から row 候補を作る（truckId, rotationNo）
 * 3. (truckId, rotationNumber) の union を取る
 * 4. 実配送 row を構築する（その cycle の operations だけで cards / legRunMap を作る）
 * 5. placeholder row を補完する (log_web_issues #235): 「その truck に少なくとも 1 つ delivery が
 *    ある」場合のみ、FR にしか無い rotation を placeholder row で埋める。truck 全体に delivery
 *    が無ければ行ごと出さない。
 * 6. 全 rows を同じソート規則で sort して返す
 *    ソートキー (V3Plans の ShiftTruck#sort_by_sort_order と同じ):
 *      truck.sortOrder → driver.sortOrder → truckId → rotationNumber
 *    固定ルート選択の有無にかかわらず同じキーを使うので、読み込み前後で既存行の並びは変わらない。
 */
export function buildVehicleRows(
  deliveries: PlanningsDeliveryWithLegRunEntity[],
  fixedRouteDetail?: ConvenienceDispatchFixedRouteDetailEntity | null,
  unassignedLegRuns: ShipmentOrderLegRunForPlanEntity[] = [],
  trucks: PlanningsTruckEntity[] = [],
  drivers: PlanningsDriverEntity[] = [],
  /** システム設定「配送リストの地点表示」。既定は座標でまとめる従来の挙動 */
  deliveryListGrouping: DeliveryListGroupingValues = 'groupByAddress',
): VehicleRowVM[] {

  // --- Step 0: 補助マップを準備 ---

  const frVehicleMap = new Map<number, ConvenienceDispatchFixedRouteVehicleEntity>();

  if (fixedRouteDetail) {
    for (const v of fixedRouteDetail.vehicles) {
      if (v.truckId == null) continue;
      frVehicleMap.set(v.truckId, v);
    }
  }

  // V3Plans 準拠のソートキー参照用
  const truckById = new Map<number, PlanningsTruckEntity>();
  for (const t of trucks) truckById.set(t.id, t);
  const driverById = new Map<number, PlanningsDriverEntity>();
  for (const d of drivers) driverById.set(d.id, d);
  const driverIdByTruckId = new Map<number, number>();
  for (const d of deliveries) {
    if (!driverIdByTruckId.has(d.truckId)) driverIdByTruckId.set(d.truckId, d.driverId);
  }

  // --- Step 0b: 固定ルート比較用インデックスを準備 ---
  // fixed route の stops を (truckId, rotation, name, kind) で高速検索できるマップ
  // - loading stop は place_name、unloading stop は store_name を使う（DB validation に合わせる）
  const getStopName = (s: ConvenienceDispatchFixedRouteStopEntity): string | null =>
    s.stopType === 'loading' ? s.placeName : s.storeName;
  const frStopIndex = new Set<string>();
  const frAllUnloadingStoreNames = new Set<string>();
  const frAllLoadingStoreNames = new Set<string>();
  if (fixedRouteDetail) {
    for (const v of fixedRouteDetail.vehicles) {
      if (v.truckId == null) continue;
      for (const s of v.stops) {
        const name = getStopName(s);
        if (!name) continue;
        frStopIndex.add(`${v.truckId}:${s.rotationNo}:${s.stopType}:${name}`);
        if (s.stopType === 'unloading') frAllUnloadingStoreNames.add(name);
        else frAllLoadingStoreNames.add(name);
      }
    }
  }

  // 全 deliveries を横断した「現在配車されている店舗名」セット (loading/unloading 別)
  const assignedUnloadingNames = new Set<string>();
  const assignedLoadingNames = new Set<string>();
  for (const d of deliveries) {
    for (const lr of d.shipmentOrderLegRuns) {
      if (lr.unloading_name) assignedUnloadingNames.add(lr.unloading_name);
      if (lr.loading_name) assignedLoadingNames.add(lr.loading_name);
    }
  }

  // 未割当案件の loading/unloading 名 セット
  const unassignedUnloadingNames = new Set<string>();
  const unassignedLoadingNames = new Set<string>();
  for (const lr of unassignedLegRuns) {
    if (lr.unloading_name) unassignedUnloadingNames.add(lr.unloading_name);
    if (lr.loading_name) unassignedLoadingNames.add(lr.loading_name);
  }

  // --- Step 1: deliveries の operations を cycle ごとに分解して row 候補を作る ---
  // 1 delivery に複数 cycle があれば複数 row 候補を生成する。

  type DeliveryRowCandidate = {
    truckId: number;
    rotationNumber: number;
    delivery: PlanningsDeliveryWithLegRunEntity;
    /** この row (cycle) に属する operations のみ */
    cycleOps: PlanningsLegRunOperationEntity[];
  };

  const deliveryRowCandidates: DeliveryRowCandidate[] = [];

  for (const d of deliveries) {
    const byCycle = new Map<number, PlanningsLegRunOperationEntity[]>();
    for (const op of d.operations) {
      const cycle = op.cycle >= 1 ? op.cycle : 1;
      if (!byCycle.has(cycle)) byCycle.set(cycle, []);
      byCycle.get(cycle).push(op);
    }

    if (byCycle.size === 0) {
      // operations が空の delivery は fallback で cycle=1 の 1 row を作る
      deliveryRowCandidates.push({ truckId: d.truckId, rotationNumber: 1, delivery: d, cycleOps: [] });
    } else {
      for (const [cycle, ops] of byCycle) {
        deliveryRowCandidates.push({ truckId: d.truckId, rotationNumber: cycle, delivery: d, cycleOps: ops });
      }
    }
  }

  // --- Step 2: fixed route detail から row 候補を作る ---

  const frRowCandidates: { truckId: number; rotationNumber: number }[] = [];
  if (fixedRouteDetail) {
    for (const v of fixedRouteDetail.vehicles) {
      if (v.truckId == null) continue;
      const rotNos = [...new Set(v.stops.map((s) => s.rotationNo))];
      for (const rn of rotNos) {
        frRowCandidates.push({ truckId: v.truckId, rotationNumber: rn });
      }
    }
  }

  // --- Step 3: (truckId, rotationNumber) の union を元に totalRotations を算出 ---

  const truckRotNos = new Map<number, Set<number>>();
  const addRotNo = (truckId: number, rotationNumber: number) => {
    if (!truckRotNos.has(truckId)) truckRotNos.set(truckId, new Set());
    truckRotNos.get(truckId).add(rotationNumber);
  };
  for (const { truckId, rotationNumber } of deliveryRowCandidates) addRotNo(truckId, rotationNumber);
  for (const { truckId, rotationNumber } of frRowCandidates) addRotNo(truckId, rotationNumber);

  const getTotalRotations = (truckId: number) => truckRotNos.get(truckId)?.size ?? 1;

  // --- Step 4a: 実配送 row を構築 ---

  const realRows: VehicleRowVM[] = deliveryRowCandidates.map(({ truckId, rotationNumber, delivery: d, cycleOps }) => {
    // この row (cycle) で使われる legRun だけを legRunMap に持つ
    const usedLegRunIds = new Set(cycleOps.map((op) => op.shipmentOrderLegRunId));
    const legRunMap = new Map<number, ShipmentOrderLegRunForPlanEntity>();
    for (const lr of d.shipmentOrderLegRuns) {
      if (lr.id != null && usedLegRunIds.has(lr.id)) legRunMap.set(lr.id, lr);
    }

    // 停車側 (buildOperationPlaces) と同じ 3 段ソート。代表座標キーは列の連続性から
    // 決まるので、arrivalAt と departureAt が同値のときに入力順へ落ちると
    // 代表キーとカードの見出しがレスポンス順に揺れる（積地の複数案件がまさにこの形）
    const sorted = [...cycleOps].sort((a, b) => (
      a.arrivalAt.localeCompare(b.arrivalAt)
      || a.departureAt.localeCompare(b.departureAt)
      || a.id - b.id
    ));
    const opCards = groupOpsToCards(sorted, legRunMap, deliveryListGrouping);

    // isRouteDeviation の再計算: 固定ルートの stops と (truckId, rotation, storeName, kind) で
    // 3+1 要素マッチを取る。一致しなければ routeDeviation=true。
    // 固定ルート未選択 (frStopIndex 空) の時は警告を出さないので false のままにする。
    if (fixedRouteDetail) {
      for (const card of opCards) {
        const stopType = card.kind === 'loading' ? 'loading' : 'unloading';
        const key = `${truckId}:${rotationNumber}:${stopType}:${card.placeName}`;
        card.isRouteDeviation = !frStopIndex.has(key);
      }
    } else {
      for (const card of opCards) {
        card.isRouteDeviation = false;
      }
    }

    // 固定ルートの各 stop に対応するカードを補完する:
    //   - legRun が配車済み → 何も出さない (該当 legRun が他車両含めどこかのカードで表示されているか、ルート外として出ている)
    //   - legRun が未割当 (unassignedUnloadingNames / unassignedLoadingNames) → 仮カード (template)
    //   - 上記いずれにも該当しない → 本日なし (no_delivery)
    //
    // 「配車済み」判定:
    //   - 降地 (unloading): 1 案件 = 1 truck の前提 → 全車合算 (assignedUnloadingNames) で判定
    //   - 積地 (loading)  : 複数 truck が同じ積地から異なる legRun を積みうる (コンビニ配送典型)
    //                       → この row の opCards だけで判定する
    const thisRowLoadingNames = new Set<string>();
    for (const op of cycleOps) {
      if (op.action !== '積') continue;
      const lr = legRunMap.get(op.shipmentOrderLegRunId);
      if (lr?.loading_name) thisRowLoadingNames.add(lr.loading_name);
    }

    const extraCards: VehicleRowCardVM[] = [];
    const frVehicle = frVehicleMap.get(truckId);
    if (frVehicle) {
      const stopsForThisRow = frVehicle.stops
        .filter((s) => s.rotationNo === rotationNumber && getStopName(s) != null)
        .sort((a, b) => a.sequenceNo - b.sequenceNo);
      for (const s of stopsForThisRow) {
        const name = getStopName(s);
        if (name == null) continue;
        const assignedForSkip = s.stopType === 'unloading' ? assignedUnloadingNames : thisRowLoadingNames;
        const unassigned = s.stopType === 'unloading' ? unassignedUnloadingNames : unassignedLoadingNames;
        if (assignedForSkip.has(name)) continue;
        if (unassigned.has(name)) {
          const matching = unassignedLegRuns.filter((lr) => {
            const lrName = s.stopType === 'unloading' ? lr.unloading_name : lr.loading_name;
            return lrName === name;
          });
          mergeOrPushExtra(extraCards, {
            kind: 'template',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, matching),
            placeName: name,
            timeLabel: null,
            itemCount: matching.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0),
            operations: [],
            legRuns: matching,
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        } else {
          mergeOrPushExtra(extraCards, {
            kind: 'no_delivery',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, []),
            placeName: name,
            timeLabel: null,
            itemCount: 0,
            operations: [],
            legRuns: [],
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        }
      }
    }

    // template / no_delivery の `legRuns` は「同じ store_name の未割当 candidate」を含むため、
    // 同 row の opCards で既に実配車されている legRun が混ざると adapter で legRunIds が
    // 重複し、同 row 内に同一 lr-{id} カードが 2 枚並ぶ (React duplicate key 警告)。
    // 既に opCards に乗っている legRun は extraCards 側から除外する。
    const opLegRunIds = new Set<number>();
    opCards.forEach((c) => {
      c.legRuns.forEach((lr) => {
        if (lr.id != null) opLegRunIds.add(lr.id);
      });
    });
    const extraCardsDeduped = extraCards.map((c) => {
      if (c.legRuns.length === 0) return c;
      const filtered = c.legRuns.filter((lr) => lr.id == null || !opLegRunIds.has(lr.id));
      if (filtered.length === c.legRuns.length) return c;
      return {
        ...c,
        legRuns: filtered,
        itemCount: filtered.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0),
      };
    });

    return {
      deliveryId: d.id,
      truckId,
      // 1 台に複数のドライバーが同じ時間帯で乗る勤務があるので、車両単位ではなく
      // その勤務 (delivery) のドライバーを行が持つ
      driverId: d.driverId ?? null,
      rotationNumber,
      totalRotations: getTotalRotations(truckId),
      driverColor: d.driverColor,
      truckColor: d.truckColor ?? null,
      // この cycle に属する legRun だけで集計する
      totalItemCount: [...legRunMap.values()].reduce((s, lr) => s + (lr.item_count ?? 0), 0),
      cards: [...opCards, ...extraCardsDeduped],
      // 展開行の計画開始に使う。HH:mm に落とさず ISO のまま渡す
      planStartAt: d.startAt,
      isPlaceholderRow: false,
    };
  });

  // --- Step 4b: placeholder row を構築 (fixed route にしか存在しない行) ---
  //
  // log_web_issues #235 で「delivery (shift_truck) がない vehicle は画面に表示しない」要件に
  // 変更したが、**vehicle 単位** の判定なので「同じ truck の中で 1 回転目に delivery があり、
  // 2 回転目は FR にしか存在しない」というケースでは 2 回転目を placeholder row として補完する。
  // → 「その truck に少なくとも 1 つ delivery がある」場合のみ、その truck 配下の不足 rotation を
  //    placeholder row で補完する。

  const deliveryKeySet = new Set(deliveryRowCandidates.map((c) => `${c.truckId}:${c.rotationNumber}`));
  const trucksWithDelivery = new Set(deliveryRowCandidates.map((c) => c.truckId));
  const placeholderRows: VehicleRowVM[] = [];

  if (fixedRouteDetail) {
    for (const { truckId, rotationNumber } of frRowCandidates) {
      if (deliveryKeySet.has(`${truckId}:${rotationNumber}`)) continue;
      // vehicle (truck) 全体に delivery が無い → 行ごと非表示
      if (!trucksWithDelivery.has(truckId)) continue;

      const vehicle = frVehicleMap.get(truckId);
      if (!vehicle) continue;
      const placeholderCards: VehicleRowCardVM[] = [];
      const stopsForThisRow = vehicle.stops
        .filter((s) => s.rotationNo === rotationNumber && getStopName(s) != null)
        .sort((a, b) => a.sequenceNo - b.sequenceNo);
      for (const s of stopsForThisRow) {
        const name = getStopName(s);
        if (name == null) continue;
        // placeholder row は実 operation を持たないので、積地の skip 対象は無し (常に表示)。
        // 降地は他 truck で配車済みなら skip (1 案件 = 1 truck)。
        const assignedForSkip = s.stopType === 'unloading' ? assignedUnloadingNames : new Set<string>();
        const unassigned = s.stopType === 'unloading' ? unassignedUnloadingNames : unassignedLoadingNames;
        if (assignedForSkip.has(name)) continue;
        if (unassigned.has(name)) {
          const matching = unassignedLegRuns.filter((lr) => {
            const lrName = s.stopType === 'unloading' ? lr.unloading_name : lr.loading_name;
            return lrName === name;
          });
          mergeOrPushExtra(placeholderCards, {
            kind: 'template',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, matching),
            placeName: name,
            timeLabel: null,
            itemCount: matching.reduce((sum, lr) => sum + (lr.item_count ?? 0), 0),
            operations: [],
            legRuns: matching,
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        } else {
          mergeOrPushExtra(placeholderCards, {
            kind: 'no_delivery',
            stopType: s.stopType,
            placeKey: buildFrPlaceKey(s.stopType, name, []),
            placeName: name,
            timeLabel: null,
            itemCount: 0,
            operations: [],
            legRuns: [],
            isRouteDeviation: false,
            isAutoAssigned: false,
          });
        }
      }

      placeholderRows.push({
        deliveryId: fixedRoutePlaceholderDeliveryId(truckId, rotationNumber),
        truckId,
        rotationNumber,
        totalRotations: getTotalRotations(truckId),
        driverColor: '#9e9e9e',
        truckColor: null,
        totalItemCount: 0,
        cards: placeholderCards,
        isPlaceholderRow: true,
      });
    }
  }

  // --- Step 5: 全 rows を同じソート規則で sort して返す ---
  // V3Plans の PlanningTrucksPresenter と同じく displayOrder 優先で並べる。
  // ソートキー:
  //   1. truck.displayOrder ?? truck.sortOrder（未登録 truck・未設定は MAX）
  //   2. driver.sortOrder（delivery 不在の placeholder 行は MAX）
  //   3. truckId（安定化用）
  //   4. rotationNumber（1回転目 → 2回転目 → ... の順）
  // API (app/controllers/api/v3/plannings/trucks_controller.rb) と同じ規則で sort:
  //   - displayOrder が設定された truck (bucket 0) を先に
  //   - bucket 内では displayOrder ?? sortOrder ?? MAX 昇順
  //   - 同値は id 昇順 (compareTrucksByDisplayOrder で集約)
  // その後に driver.sortOrder / truckId / rotationNumber の二次キー。
  const MAX = Number.MAX_SAFE_INTEGER;
  const driverSortOrderOf = (truckId: number) => {
    const did = driverIdByTruckId.get(truckId);
    if (did == null) return MAX;
    return driverById.get(did)?.sortOrder ?? MAX;
  };

  const sorted = [...realRows, ...placeholderRows].sort((a, b) => {
    const aTruck = truckById.get(a.truckId);
    const bTruck = truckById.get(b.truckId);
    if (aTruck && bTruck) {
      const truckCmp = compareTrucksByDisplayOrder(aTruck, bTruck);
      if (truckCmp !== 0) return truckCmp;
    } else if (aTruck) {
      return -1;
    } else if (bTruck) {
      return 1;
    }
    const dDiff = driverSortOrderOf(a.truckId) - driverSortOrderOf(b.truckId);
    if (dDiff !== 0) return dDiff;
    if (a.truckId !== b.truckId) return a.truckId - b.truckId;
    return a.rotationNumber - b.rotationNumber;
  });

  // --- Step 6: 各勤務 (delivery) の rotation 番号を 1..N に振り直す（繰り上げ）---
  // op.cycle ベースのままだと、回転途中を削除したときに "回転3" が残って
  // "回転2" がない歯抜け表示になる。サンプル仕様 (回転3 → 回転2) に合わせて
  // 表示用に sourceRowsRaw 段階で連番を振り直す。
  //
  // truck 単位で通し番号にすると、1 台に 2 つの勤務が乗るとき 2 人目の回転が 2・3… に
  // なり、「◯回転目に追加・削除」の対象が別の勤務にずれる（実測 /map/2026-07-03 の
  // truck 149: 内部値 1・3・2）。回転は勤務の中の概念なので勤務ごとに振る。
  //
  // 注: row.rotationNumber は後段の (a) card ID 生成 (b) timeline_operations の cycle
  // にも使われるので、両方とも連番ベースになる。次回の auto-dispatch 時に backend の
  // op.cycle が連番値で上書きされて自然に整合する。
  // placeholder 行 (FR 由来・回転追加) は実体が無く deliveryId が負のダミーなので、
  // 同じ truck の実在 delivery に属するものとして数える（別勤務として 1 から振り直すと、
  // 「1 回転目の下に 1 回転目」が並ぶ）。adapter.ts のグループ化と同じ規則。
  const realDeliveryIdByTruck = new Map<number, number>();
  for (const row of sorted) {
    if (row.isPlaceholderRow) continue;
    if (!realDeliveryIdByTruck.has(row.truckId)) realDeliveryIdByTruck.set(row.truckId, row.deliveryId);
  }
  const groupKeyOf = (row: VehicleRowVM) => {
    if (!row.isPlaceholderRow) return row.deliveryId;
    // 「回転を追加」で足した行は追加元の勤務に属する。固定ルート由来の placeholder は
    // extraOfDeliveryId を持たないので truck の実在 delivery から引く
    return row.extraOfDeliveryId ?? realDeliveryIdByTruck.get(row.truckId) ?? row.deliveryId;
  };

  const totalsByDelivery = new Map<number, number>();
  const seqByDelivery = new Map<number, number>();
  for (const row of sorted) {
    const key = groupKeyOf(row);
    totalsByDelivery.set(key, (totalsByDelivery.get(key) ?? 0) + 1);
  }
  return sorted.map((row) => {
    const key = groupKeyOf(row);
    const seq = (seqByDelivery.get(key) ?? 0) + 1;
    seqByDelivery.set(key, seq);
    return {
      ...row,
      rotationNumber: seq,
      totalRotations: totalsByDelivery.get(key) ?? row.totalRotations,
    };
  });
}
